import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./Projects.css";
import { API_BASE } from "../../../ApiUrl";
import ProjectTabs from "./ProjectTabs";

const PROJECTS_API_URL = `${API_BASE}/projects/`;
const TENANT_RECORDS_API_URL = `${API_BASE}/tenant-records/`;
const ORGANIZATIONS_API_URL = `${API_BASE}/organizations/`;
const TENANT_LOCATIONS_API_URL = `${API_BASE}/tenant-locations/`;
const REFERENCE_VALUES_API_URL = `${API_BASE}/reference-values/`;
const SCOPE_CATALOGS_API_URL = `${API_BASE}/scope-catalogs/`;

const CONFIDENTIALITY_OPTIONS = [
  { value: "PUBLIC", label: "Public" },
  { value: "INTERNAL", label: "Internal" },
  { value: "CONFIDENTIAL", label: "Confidential" },
  { value: "RESTRICTED", label: "Restricted" },
];

const STATUS_OPTIONS = [
  { value: "DRAFT", label: "Draft" },
  { value: "OPEN", label: "Open" },
  { value: "ACTIVE", label: "Active" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELLED", label: "Cancelled" },
];

const COUNTRY_CODE_OPTIONS = [
  { value: "IN", label: "IN — India" },
  { value: "US", label: "US — United States" },
  { value: "GB", label: "GB — United Kingdom" },
  { value: "AE", label: "AE — United Arab Emirates" },
  { value: "SG", label: "SG — Singapore" },
  { value: "AU", label: "AU — Australia" },
];

const emptyProject = {
  id: null,
  business_unit: "",
  location: "",
  project_code: "",
  project_name: "",
  project_type: "",
  client_organization: "",
  industry: "",
  scope_catalog_entries: [],
  country_code: "",
  city: "",
  start_date: "",
  end_date: "",
  confidentiality_classification: "INTERNAL",
  status: "DRAFT",
  description: "",
};

const toDateInput = (v) => (v ? String(v).substring(0, 10) : "");

const AddProject = () => {
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState([{ ...emptyProject }]);
  const [tenantId, setTenantId] = useState(null);

  const [organizations, setOrganizations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [allScopeCatalogs, setAllScopeCatalogs] = useState([]);

  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingIndustries, setLoadingIndustries] = useState(false);
  const [loadingScopes, setLoadingScopes] = useState(false);

  const getTenantUser = () => {
    try {
      const tenantUser = localStorage.getItem("tenant_user");
      return tenantUser ? JSON.parse(tenantUser) : null;
    } catch (error) {
      console.error("Error reading tenant_user:", error);
      return null;
    }
  };

  // ---- Get tenant id ----
  useEffect(() => {
    const tenantUser = getTenantUser();
    if (tenantUser?.tenant_id) {
      setTenantId(Number(tenantUser.tenant_id));
    }
  }, []);

  // ---- Fetch existing projects ----
  useEffect(() => {
    if (tenantId) fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const fetchProjects = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        TENANT_RECORDS_API_URL,
        {
          tenant_id: tenantId,
          tables: ["projects"],
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const result = response.data;

      let rows = [];
      if (result?.data?.projects) {
        rows = result.data.projects;
      } else if (result?.projects) {
        rows = result.projects;
      } else if (Array.isArray(result?.data)) {
        rows = result.data;
      } else if (Array.isArray(result)) {
        rows = result;
      }

      setProjects(rows);
    } catch (error) {
      console.error("Error fetching projects:", error);
      Swal.fire({
        icon: "error",
        title: "Unable to load projects",
        text:
          error.response?.data?.detail ||
          "Something went wrong while loading projects.",
      });
    } finally {
      setLoading(false);
    }
  };

  // ---- Fetch organizations ----
  useEffect(() => {
    const fetchOrganizations = async () => {
      setLoadingOrganizations(true);
      const tenantUser = getTenantUser();

      try {
        const response = await axios.get(ORGANIZATIONS_API_URL, {
          params: tenantUser?.tenant_id
            ? { tenant: tenantUser.tenant_id }
            : undefined,
        });

        const result = response.data;
        const orgData = result?.data
          ? result.data
          : Array.isArray(result)
          ? result
          : result?.results || [];

        const filtered = tenantUser?.tenant_id
          ? orgData.filter(
              (o) => String(o.tenant) === String(tenantUser.tenant_id)
            )
          : orgData;

        setOrganizations(filtered);
      } catch (error) {
        console.error("Error fetching organizations:", error);
      } finally {
        setLoadingOrganizations(false);
      }
    };

    fetchOrganizations();
  }, []);

  // ---- Fetch locations ----
  useEffect(() => {
    const fetchLocations = async () => {
      setLoadingLocations(true);
      const tenantUser = getTenantUser();

      try {
        const response = await axios.get(TENANT_LOCATIONS_API_URL, {
          params: tenantUser?.tenant_id
            ? { tenant: tenantUser.tenant_id }
            : undefined,
        });

        const result = response.data;
        const locationData = result?.data
          ? result.data
          : Array.isArray(result)
          ? result
          : result?.results || [];

        const filtered = tenantUser?.tenant_id
          ? locationData.filter(
              (l) => String(l.tenant) === String(tenantUser.tenant_id)
            )
          : locationData;

        setLocations(filtered);
      } catch (error) {
        console.error("Error fetching tenant locations:", error);
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchLocations();
  }, []);

  // ---- Fetch industries ----
  useEffect(() => {
    const fetchIndustries = async () => {
      setLoadingIndustries(true);

      try {
        const response = await fetch(REFERENCE_VALUES_API_URL, {
          method: "GET",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        });

        const result = await response.json();

        if (response.ok) {
          const industryData = result?.data || [];
          const filteredIndustries = industryData.filter(
            (item) =>
              item.option_set_value?.option_type === "INDUSTRY" ||
              item.option_set?.option_type === "INDUSTRY" ||
              item.option_type === "INDUSTRY"
          );
          setIndustries(filteredIndustries);
        }
      } catch (error) {
        console.error("Error fetching industries:", error);
      } finally {
        setLoadingIndustries(false);
      }
    };

    fetchIndustries();
  }, []);

  // ---- Fetch scope catalogs ----
  useEffect(() => {
    const fetchScopeCatalogs = async () => {
      setLoadingScopes(true);

      try {
        const response = await fetch(SCOPE_CATALOGS_API_URL, {
          method: "GET",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        });

        const result = await response.json();

        if (response.ok) setAllScopeCatalogs(result?.data || []);
      } catch (error) {
        console.error("Error fetching scope catalogs:", error);
      } finally {
        setLoadingScopes(false);
      }
    };

    fetchScopeCatalogs();
  }, []);

  // ---- Form handlers ----
  const handleChange = (index, e) => {
    const { name, value } = e.target;
    setFormData((prev) =>
      prev.map((p, i) => (i === index ? { ...p, [name]: value } : p))
    );
  };

  const handleScopeToggle = (index, scopeValue) => {
    setFormData((prev) =>
      prev.map((p, i) => {
        if (i !== index) return p;
        const has = p.scope_catalog_entries.includes(scopeValue);
        return {
          ...p,
          scope_catalog_entries: has
            ? p.scope_catalog_entries.filter((v) => v !== scopeValue)
            : [...p.scope_catalog_entries, scopeValue],
        };
      })
    );
  };

  const handleAdd = () => {
    setFormData([{ ...emptyProject }]);
    setIsEditing(false);
    setEditingId(null);
    setShowForm(true);
  };

  const handleAddAnother = () => {
    setFormData((prev) => [...prev, { ...emptyProject }]);
  };

  const handleRemoveForm = (index) => {
    setFormData((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEdit = (p) => {
    setFormData([
      {
        id: p.id,
        business_unit: p.business_unit ? String(p.business_unit) : "",
        location: p.location ? String(p.location) : "",
        project_code: p.project_code || "",
        project_name: p.project_name || "",
        project_type: p.project_type || "",
        client_organization: p.client_organization
          ? String(p.client_organization)
          : "",
        industry: p.industry ? String(p.industry) : "",
        scope_catalog_entries: Array.isArray(p.scope_catalog_entries)
          ? p.scope_catalog_entries.map((s) =>
              typeof s === "object" ? String(s.id) : String(s)
            )
          : [],
        country_code: p.country_code || "",
        city: p.city || "",
        start_date: toDateInput(p.start_date),
        end_date: toDateInput(p.end_date),
        confidentiality_classification:
          p.confidentiality_classification || "INTERNAL",
        status: p.status || "DRAFT",
        description: p.description || "",
      },
    ]);
    setEditingId(p.id);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = async (p) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete Project?",
      text: `Are you sure you want to delete "${
        p.project_name || p.project_code || "this project"
      }"?`,
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${PROJECTS_API_URL}${p.id}/`);

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Project deleted successfully.",
        timer: 1500,
        showConfirmButton: false,
      });

      fetchProjects();
    } catch (error) {
      console.error("Error deleting project:", error);
      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text:
          error.response?.data?.detail ||
          "Unable to delete the project.",
      });
    }
  };

  // ---- Validation ----
  const validateRow = (p, index) => {
    const label = `Project ${index + 1}`;
    const isEmptyRow =
      !p.project_code.trim() && !p.project_name.trim() && !p.id;
    if (isEmptyRow) return true;

    if (!p.project_code.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Required Field Missing",
        text: `Please provide a Project Code for ${label}.`,
      });
      return false;
    }

    if (!p.project_name.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Required Field Missing",
        text: `Please provide a Project Name for ${label}.`,
      });
      return false;
    }

    return true;
  };

  const buildPayload = (p) => ({
    project_code: p.project_code.trim(),
    project_name: p.project_name.trim(),
    project_type: (p.project_type || "").trim(),
    country_code: p.country_code || "",
    city: (p.city || "").trim(),
    start_date: p.start_date || null,
    end_date: p.end_date || null,
    confidentiality_classification: p.confidentiality_classification,
    status: p.status,
    description: (p.description || "").trim(),
    tenant: Number(tenantId),
    business_unit: p.business_unit ? Number(p.business_unit) : null,
    location: p.location ? Number(p.location) : null,
    client_organization: p.client_organization
      ? Number(p.client_organization)
      : null,
    industry: p.industry ? Number(p.industry) : null,
    scope_catalog_entries: p.scope_catalog_entries.map((id) => Number(id)),
  });

  const extractError = (error) => {
    if (error.response?.data) {
      const d = error.response.data;
      if (typeof d === "string") return d;
      if (d.detail) return d.detail;
      if (d.message) return d.message;
      if (d.errors) {
        return Object.entries(d.errors)
          .map(
            ([field, errors]) =>
              `${field}: ${Array.isArray(errors) ? errors.join(", ") : errors}`
          )
          .join(" | ");
      }
      return JSON.stringify(d);
    }
    return error.message || "Unknown error";
  };

  // ---- Submit ----
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tenantId) {
      Swal.fire({
        icon: "error",
        title: "Tenant ID missing",
        text: "Unable to identify the current tenant.",
      });
      return;
    }

    for (let i = 0; i < formData.length; i++) {
      if (!validateRow(formData[i], i)) return;
    }

    const activeRows = formData.filter(
      (p) => p.project_code.trim() || p.project_name.trim() || p.id
    );

    try {
      setLoading(true);

      if (isEditing) {
        const payload = buildPayload(formData[0]);
        await axios.put(`${PROJECTS_API_URL}${editingId}/`, payload, {
          headers: { "Content-Type": "application/json" },
        });

        await Swal.fire({
          icon: "success",
          title: "Updated Successfully",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        for (const p of activeRows) {
          await axios.post(PROJECTS_API_URL, buildPayload(p), {
            headers: { "Content-Type": "application/json" },
          });
        }

        await Swal.fire({
          icon: "success",
          title: "Created Successfully",
          text: `${activeRows.length} project${
            activeRows.length > 1 ? "s" : ""
          } created successfully.`,
          timer: 1500,
          showConfirmButton: false,
        });
      }

      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      setFormData([{ ...emptyProject }]);

      await fetchProjects();
    } catch (error) {
      console.error("Error saving project:", error);

      Swal.fire({
        icon: "error",
        title: isEditing ? "Update failed" : "Creation failed",
        text: extractError(error),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setIsEditing(false);
    setEditingId(null);
    setFormData([{ ...emptyProject }]);
  };

  // ---- Lookup helpers for table ----
  const getOrgName = (id) => {
    const o = organizations.find((x) => String(x.id) === String(id));
    return o?.name || o?.organization_name || (id ? `Org ${id}` : "-");
  };

  const getLocationLabel = (id) => {
    const l = locations.find((x) => String(x.id) === String(id));
    return l?.location_type || (id ? `Location ${id}` : "-");
  };

  const getIndustryLabel = (id) => {
    const i = industries.find((x) => String(x.id) === String(id));
    return (
      i?.value || i?.label || i?.name || (id ? `Industry ${id}` : "-")
    );
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="tenant-project-page">
          <ProjectTabs />

          {!showForm ? (
            <>
              {/* ==================================================
                  LIST HEADER
              ================================================== */}

              <div className="tenant-project-page-header">
                <div>
                  <h2>Projects</h2>
                </div>

                <button
                  type="button"
                  className="tenant-project-add-top-btn"
                  onClick={handleAdd}
                >
                  + Add Project
                </button>
              </div>

              {/* ==================================================
                  TABLE
              ================================================== */}

              <div className="tenant-project-list-card">
                {loading ? (
                  <div className="tenant-project-loading">
                    Loading projects...
                  </div>
                ) : projects.length === 0 ? (
                  <div className="tenant-project-empty">
                    <div className="tenant-project-empty-icon">+</div>
                    <h3>No Projects Found</h3>
                    <p>
                      There are no projects available for the current tenant.
                    </p>
                    <button
                      type="button"
                      className="tenant-project-empty-add-btn"
                      onClick={handleAdd}
                    >
                      + Add Project
                    </button>
                  </div>
                ) : (
                  <div className="tenant-project-table-wrapper">
                    <table className="tenant-project-table">
                      <thead>
                        <tr>
                          <th>Code</th>
                          <th>Name</th>
                          <th>Type</th>
                          <th>Business Unit</th>
                          <th>Client</th>
                          <th>Industry</th>
                          <th>Location</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {projects.map((p) => (
                          <tr key={p.id}>
                            <td>
                              <span className="tenant-project-code-pill">
                                {p.project_code || "-"}
                              </span>
                            </td>

                            <td>
                              <strong>{p.project_name || "-"}</strong>
                            </td>

                            <td>{p.project_type || "-"}</td>

                            <td className="tenant-project-muted">
                              {getOrgName(p.business_unit)}
                            </td>

                            <td className="tenant-project-muted">
                              {getOrgName(p.client_organization)}
                            </td>

                            <td className="tenant-project-muted">
                              {getIndustryLabel(p.industry)}
                            </td>

                            <td className="tenant-project-muted">
                              {getLocationLabel(p.location)}
                            </td>

                            <td>
                              <span
                                className={`tenant-project-status ${
                                  (p.status || "").toLowerCase()
                                }`}
                              >
                                {p.status || "-"}
                              </span>
                            </td>

                            <td>
                              <div className="tenant-project-actions">
                                <button
                                  type="button"
                                  className="tenant-project-edit-btn"
                                  onClick={() => handleEdit(p)}
                                >
                                  Edit
                                </button>

                                <button
                                  type="button"
                                  className="tenant-project-delete-btn"
                                  onClick={() => handleDelete(p)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* ==================================================
                  ADD / EDIT HEADER
              ================================================== */}

              <div className="tenant-project-page-header">
                <div>
                  <h2>
                    {isEditing ? "Edit Project" : "Create Projects"}
                  </h2>
                </div>

                <button
                  type="button"
                  className="tenant-project-back-btn"
                  onClick={handleCancel}
                >
                  ← Back
                </button>
              </div>

              {/* ==================================================
                  FORM
              ================================================== */}

              <form onSubmit={handleSubmit}>
                {formData.map((p, index) => (
                  <div
                    className="tenant-project-card"
                    key={index}
                    style={{ marginBottom: "20px" }}
                  >
                    <div className="tenant-project-form-body">
                      <div className="tenant-project-record-header">
                        <h4>Project {index + 1}</h4>

                        {!isEditing && formData.length > 1 && (
                          <button
                            type="button"
                            className="tenant-project-remove-btn"
                            onClick={() => handleRemoveForm(index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div className="tenant-project-form-grid">
                        <div className="tenant-project-form-group">
                          <label>Business Unit</label>
                          <select
                            name="business_unit"
                            value={p.business_unit}
                            onChange={(e) => handleChange(index, e)}
                            disabled={loadingOrganizations}
                          >
                            <option value="">
                              {loadingOrganizations
                                ? "Loading..."
                                : "Select business unit"}
                            </option>
                            {organizations.map((org) => (
                              <option key={org.id} value={org.id}>
                                {org.name ||
                                  org.organization_name ||
                                  `Organization ${org.id}`}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="tenant-project-form-group">
                          <label>Location</label>
                          <select
                            name="location"
                            value={p.location}
                            onChange={(e) => handleChange(index, e)}
                            disabled={loadingLocations}
                          >
                            <option value="">
                              {loadingLocations
                                ? "Loading..."
                                : "Select location"}
                            </option>
                            {locations.map((loc) => (
                              <option key={loc.id} value={loc.id}>
                                {loc.location_type || `Location ${loc.id}`}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="tenant-project-form-group">
                          <label>Project Code</label>
                          <input
                            type="text"
                            name="project_code"
                            value={p.project_code}
                            onChange={(e) => handleChange(index, e)}
                            placeholder="e.g. PRJ-001"
                            maxLength={50}
                          />
                        </div>

                        <div className="tenant-project-form-group">
                          <label>Project Name</label>
                          <input
                            type="text"
                            name="project_name"
                            value={p.project_name}
                            onChange={(e) => handleChange(index, e)}
                            placeholder="Enter project name"
                            maxLength={250}
                          />
                        </div>

                        <div className="tenant-project-form-group">
                          <label>Project Type</label>
                          <input
                            type="text"
                            name="project_type"
                            value={p.project_type}
                            onChange={(e) => handleChange(index, e)}
                            placeholder="e.g. Construction"
                            maxLength={100}
                          />
                        </div>

                        <div className="tenant-project-form-group">
                          <label>Client Organization</label>
                          <select
                            name="client_organization"
                            value={p.client_organization}
                            onChange={(e) => handleChange(index, e)}
                            disabled={loadingOrganizations}
                          >
                            <option value="">
                              {loadingOrganizations
                                ? "Loading..."
                                : "Select client organization"}
                            </option>
                            {organizations.map((org) => (
                              <option key={org.id} value={org.id}>
                                {org.name ||
                                  org.organization_name ||
                                  `Organization ${org.id}`}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="tenant-project-form-group">
                          <label>Industry</label>
                          <select
                            name="industry"
                            value={p.industry}
                            onChange={(e) => handleChange(index, e)}
                            disabled={loadingIndustries}
                          >
                            <option value="">
                              {loadingIndustries
                                ? "Loading..."
                                : "Select industry"}
                            </option>
                            {industries.map((item) => (
                              <option key={item.id} value={item.id}>
                                {item.value ||
                                  item.label ||
                                  item.name ||
                                  `Reference ${item.id}`}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="tenant-project-form-group">
                          <label>Country Code</label>
                          <select
                            name="country_code"
                            value={p.country_code}
                            onChange={(e) => handleChange(index, e)}
                          >
                            <option value="">Select country</option>
                            {COUNTRY_CODE_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="tenant-project-form-group">
                          <label>City</label>
                          <input
                            type="text"
                            name="city"
                            value={p.city}
                            onChange={(e) => handleChange(index, e)}
                            placeholder="e.g. Mumbai"
                            maxLength={120}
                          />
                        </div>

                        <div className="tenant-project-form-group">
                          <label>Start Date</label>
                          <input
                            type="date"
                            name="start_date"
                            value={p.start_date}
                            onChange={(e) => handleChange(index, e)}
                          />
                        </div>

                        <div className="tenant-project-form-group">
                          <label>End Date</label>
                          <input
                            type="date"
                            name="end_date"
                            value={p.end_date}
                            onChange={(e) => handleChange(index, e)}
                          />
                        </div>

                        <div className="tenant-project-form-group">
                          <label>Confidentiality</label>
                          <select
                            name="confidentiality_classification"
                            value={p.confidentiality_classification}
                            onChange={(e) => handleChange(index, e)}
                          >
                            {CONFIDENTIALITY_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="tenant-project-form-group">
                          <label>Status</label>
                          <select
                            name="status"
                            value={p.status}
                            onChange={(e) => handleChange(index, e)}
                          >
                            {STATUS_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="tenant-project-form-group tenant-project-form-group--full">
                          <label>Scope Catalog Entries</label>
                          <div className="tenant-project-scope-chips">
                            {loadingScopes ? (
                              <span className="tenant-project-scope-loading">
                                Loading...
                              </span>
                            ) : allScopeCatalogs.length === 0 ? (
                              <span className="tenant-project-scope-loading">
                                No scope catalogs available
                              </span>
                            ) : (
                              allScopeCatalogs.map((opt) => {
                                const optId = String(opt.id);
                                const optLabel =
                                  opt.name ||
                                  opt.scope_name ||
                                  opt.title ||
                                  `Scope ${opt.id}`;
                                const checked =
                                  p.scope_catalog_entries.includes(optId);

                                return (
                                  <label
                                    key={opt.id}
                                    className={`tenant-project-scope-chip${
                                      checked
                                        ? " tenant-project-scope-chip--active"
                                        : ""
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() =>
                                        handleScopeToggle(index, optId)
                                      }
                                    />
                                    <span>{optLabel}</span>
                                  </label>
                                );
                              })
                            )}
                          </div>
                        </div>

                        <div className="tenant-project-form-group tenant-project-form-group--full">
                          <label>Description</label>
                          <textarea
                            name="description"
                            value={p.description}
                            onChange={(e) => handleChange(index, e)}
                            placeholder="Enter project description"
                            rows={3}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {!isEditing && (
                  <button
                    type="button"
                    className="tenant-project-add-btn"
                    onClick={handleAddAnother}
                    style={{ marginBottom: "20px" }}
                  >
                    <span>+</span>
                    Add Another Project
                  </button>
                )}

                <div className="tenant-project-form-actions">
                  <button
                    type="button"
                    className="tenant-project-cancel-btn"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="tenant-project-save-btn"
                    disabled={loading}
                  >
                    {loading
                      ? "Saving..."
                      : isEditing
                      ? "Update Project"
                      : "Save Projects"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddProject;